/// <reference path="../../typings/browser.d.ts" />
/// <reference path="../../customtypings/window.d.ts" />

/**
 * Monitoring Room service
 */

namespace Application.Services {
    'use strict';

    import MonitoringRoom = Models.MonitoringRoom;
    import MonitorVideoStream = Models.MonitorVideoStream;
    import IMonitor = Models.IMonitor;
    import IHalList = Models.IHalList;
    import IHalElement = Models.IHalElement;
    import HalConverter = Models.HalConverter;
    import VideoFeed = Application.Models.VideoFeed;

    /**
     * MonitoringRoomService
     */
    export class MonitoringRoomService {
        public static $name: string = "monitoringRoomService";
        public static $inject: string[] = ['$http', '$q', 'streamPipeService', 'backendService', 'stompService'];

        private http: ng.IHttpService;
        private qService: ng.IQService;
        private streamPipeService: StreamPipeService;
        private backendService: BackendService;

        private backendBaseUrl: string = window.backendUrl;
        // private servicePath: string = 'connrooms/';
        private servicePath: string = '';
        private monitoringRoomsBackendUrl: string = this.backendBaseUrl + this.servicePath + 'monitorRooms';
        private monitorsBackendUrl: string = this.backendBaseUrl + this.servicePath + 'monitors';
        private monitorPagesBackendUrl: string = this.backendBaseUrl + this.servicePath + 'monitorPages';
        private monitorPageGridCache: GridPageMap = {};
        private gridPagesByRoom: GridPagesByRoomMap = {};
        private allGridRooms: Models.IGridRoom[];


        constructor($http: ng.IHttpService,
                    $q: ng.IQService,
                    streamPipeService: StreamPipeService,
                    backendService: BackendService,
                    private stompService: StompService) {
            this.http = $http;
            this.qService = $q;
            this.streamPipeService = streamPipeService;
            this.backendService = backendService;
            this.stompService.register(Models.CONNECTION_ROOM_COMPONENT, Models.ALL_OPERATIONS,
                (flexEvent: Models.IFlexEvent) => {
                    if (flexEvent.operation !== Models.TRENDING_ON_OPERATION &&
                        flexEvent.operation !== Models.TRENDING_OFF_OPERATION) {
                        if (flexEvent.operation === Models.CREATE_MONITOR_ROOM_OPERATION ||
                            flexEvent.operation === Models.DELETE_MONITOR_ROOM_OPERATION) {
                            this.allGridRooms = undefined;
                        } else {
                            //TODO: Improve and remove only what has changed
                            this.gridPagesByRoom = {};
                            this.monitorPageGridCache = {};
                        }
                    }
                }
            );
        }

        public getAvailableStreams(callback: (monitorVideoStreams: MonitorVideoStream[]) => void): void {
            this.getAvailableStreamsPromise()
                .then((response: Models.IHttpMonitorsResponse) => {
                    callback(response.data);
                });
        }

        public getAvailableStreamsPromise(): ng.IPromise<Models.IMonitorVideoStream[]> {
            return this.http
                .get(this.monitorsBackendUrl + '/availableStreams')
                .success((monitorVideoStreams: Models.MonitorVideoStream[]) => {
                    return monitorVideoStreams;
                });
        }

        public getStreamByHref(href: string): ng.IPromise<MonitorVideoStream> {
            return this.http
                .get(this.monitorsBackendUrl + '/availableStreams')
                .success((monitorVideoStreams: Models.MonitorVideoStream[]) => {
                    monitorVideoStreams.forEach((monitorVideoStream: MonitorVideoStream) => {
                        if (href === monitorVideoStream.href)
                            return monitorVideoStream;
                    });
                });
        }

        public getByHref(href: string, callback: (result: MonitoringRoom) => void): void {
            this.getMonitorRoomPromise(href)
                .then((element: MonitoringRoom) => {
                    callback(element);
                });
        }

        public getAllMonitoringRooms(callback: (messages: MonitoringRoom[]) => void ): void {
            this.getAllMonitoringRoomsPromise()
                .then((elements: MonitoringRoom[]) => {
                    callback(elements);
                });
        }

        public saveMonitoringRoom(monitoringRoom: MonitoringRoom): ng.IPromise<MonitoringRoom> {
            if (monitoringRoom.href && monitoringRoom.href !== '')
                return this.updateMonitoringRoom(monitoringRoom);
            else
                return this.createMonitoringRoom(monitoringRoom);
        }

        public deleteMonitoringRoom(monitoringRoom: MonitoringRoom): ng.IPromise<{}> {
            return this.backendService.delete(monitoringRoom.href);
        }

        public saveMonitor(monitor: IMonitor): ng.IPromise<IMonitor> {
            if (monitor.href && monitor.href !== '')
                return this.updateMonitor(monitor);
            else
                return this.createMonitor(monitor);
        }

        public removeMonitor(monitor: IMonitor): ng.IPromise<{}> {
            return this.backendService.delete(monitor.href);
        }

        public getMonitor(id: number): ng.IPromise<IMonitor> {
            return this.backendService
                .get(this.monitorsBackendUrl + "/" + id.toString(), true, undefined, false, true)
                .then((result: ng.IHttpPromiseCallbackArg<IHalElement<IMonitor>>) => {
                    let halElement: IHalElement<IMonitor> = result.data;
                    let converted: IMonitor = HalConverter.convertElement(halElement, Models.Monitor.Empty);
                    return converted;
                });
        }

        public reorderMonitors(monitorRoomHref: string, monitorsOrder: number[]): void {
            this.backendService.post(monitorRoomHref + "/monitorsOrder", { monitorsOrder });
        }

        public setSourceBlock(monitor: Models.Monitor, block: Models.Block): ng.IPromise<{}> {
            return this.backendService.post(monitor.href + "/blocks", {sourceBlock: block.href});
        }

        public setDestinationBlock(monitor: Models.Monitor, block: Models.Block): ng.IPromise<{}> {
            return this.backendService.post(monitor.href + "/blocks", {destinationBlock: block.href});
        }

        public notifyTrendingSwitch(monitorRoomHref: number, active: boolean): ng.IPromise<void> {
            return this.backendService
            .post(this.monitoringRoomsBackendUrl + "/" + monitorRoomHref.toString() + "/trending/" + active, {})
            .then ((result: ng.IHttpPromiseCallbackArg<IHalElement<void>>) => {
                return;
            });
        }

        public getAllMonitoringRoomsPromise(): ng.IPromise<MonitoringRoom[]> {
            return this.backendService
                .get(this.monitoringRoomsBackendUrl + "/filtered", true, undefined, false, true)
                .then((result: ng.IHttpPromiseCallbackArg<IHalList<MonitoringRoom>>) => {
                    let halElement: IHalList<MonitoringRoom> = result.data;
                    let converted: MonitoringRoom[] = HalConverter.convertList(halElement, MonitoringRoom.Empty);
                    return this.getRoomsWithMonitors(converted);
                });
        }

        public getRoomsWithMonitors(monitorRooms: MonitoringRoom[]): ng.IPromise<MonitoringRoom[]> {
            let roomIds: number[] = monitorRooms.map((element: MonitoringRoom) => 
                Common.Utils.idFromHref(element.href)
            );
            return this.backendService
            .post(this.monitoringRoomsBackendUrl + '/monitors/list', roomIds)
                .then((nestedResult: ng.IHttpPromiseCallbackArg<IHalList<IMonitor[]>[]>) => {
                    let nestedHalElements: IHalList<IMonitor>[] = nestedResult.data;
                    nestedHalElements.forEach((nestedHalElement: IHalList<IMonitor>, index: number) => {
                        let nestedConverted: IMonitor[] = HalConverter.convertList(nestedHalElement, Models.Monitor.Empty);
                        monitorRooms[index].monitors = nestedConverted;
                    });
                    return monitorRooms;
                });
        }

        /**
         * BEGIN OF NEW METHODS FOR GRID
         */
        public getAllGridRooms(): ng.IPromise<Models.IGridRoom[]> {
            if (this.allGridRooms !== undefined) {
                return this.qService.resolve(this.allGridRooms);
            }
            return this.backendService.get(this.monitoringRoomsBackendUrl + '/filtered', true, undefined, false, true).then(
                (result: ng.IHttpPromiseCallbackArg<IHalList<Models.IGridRoom>>) => {
                    let halList: IHalList<Models.IGridRoom> = result.data;
                    let rooms: Models.IGridRoom[] = HalConverter.convertList(halList, Models.GridRoom.Empty);
                    for (let room of rooms) {
                        room.id = Common.Utils.idFromHref(room.href);
                    }
                    this.allGridRooms = rooms;
                    return rooms;
                });
        }

        public getAllGridPagesByRoomId(id: number): ng.IPromise<Models.IGridPage[]> {
            if (this.gridPagesByRoom[id] !== undefined)
                return this.qService.resolve(this.gridPagesByRoom[id]);
            return this.backendService.get(this.monitoringRoomsBackendUrl + '/' + id + '/monitorPages').then(
                (result: ng.IHttpPromiseCallbackArg<IHalList<Models.IGridPage>>) => {
                    let halList: IHalList<Models.IGridPage> = result.data;
                    let pages: Models.IGridPage[] = HalConverter.convertList(halList, Models.GridPage.Empty);
                    for (let page of pages)
                        this.burnishPage(page);
                    pages.sort((a: Models.IGridPage, b: Models.IGridPage) => {
                        return (a.numOrder < b.numOrder) ? -1 : 1;
                    });
                    this.gridPagesByRoom[id] = pages;
                    return pages;
                });
        }

        public getCachedMonitorPageGridByPageId(id: number): Models.IMonitorPageGrid {
            if (this.monitorPageGridCache[id] !== undefined) {
                return this.monitorPageGridCache[id];
            } else {
                return undefined;
            }
        }

        public getMonitorPageGridByPageId(id: number): ng.IPromise<Models.IMonitorPageGrid> {
            return this.backendService.get(this.monitorPagesBackendUrl + '/' + id + '/grid').then(
                (result: ng.IHttpPromiseCallbackArg<Models.IMonitorPageGrid>) => {
                    result.data.monitorPage.monitors.forEach((monitor: Models.IGridMonitor) => {
                        if (monitor.type === 'SwitchPort' && typeof monitor.switchId !== 'undefined') {
                            monitor.sink = monitor.switchId;
                        } else {
                            if (monitor.videoFeed) {
                                monitor.videoFeed.thumbnailUrl = VideoFeed.setThumbnailPerStatus(monitor.videoFeed);
                            }
                        }

                    });
                    this.monitorPageGridCache[id] = result.data;
                    return result.data;
                });
        }

        public removeRow(page: number, row: number): ng.IPromise<Models.IGridPage> {
            this.monitorPageGridCache[page] = undefined;
            return this.backendService.delete(this.monitorPagesBackendUrl + '/' + page + '/rows/' + row).then(
                (result: ng.IHttpPromiseCallbackArg<IHalElement<Models.IGridPage>>) => {
                    let p: Models.IGridPage = HalConverter.convertElement(result.data, Models.GridPage.Empty);
                    this.burnishPage(p);
                    return p;
                });
        }

        public removeColumn(page: number, column: number): ng.IPromise<Models.IGridPage> {
            this.monitorPageGridCache[page] = undefined;
            return this.backendService.delete(this.monitorPagesBackendUrl + '/' + page + '/columns/' + column).then(
                (result: ng.IHttpPromiseCallbackArg<IHalElement<Models.IGridPage>>) => {
                    let p: Models.IGridPage = HalConverter.convertElement(result.data, Models.GridPage.Empty);
                    this.burnishPage(p);
                    return p;
                });
        }

        public addRow(page: number): ng.IPromise<Models.IGridPage> {
            this.monitorPageGridCache[page] = undefined;
            return this.backendService.post(this.monitorPagesBackendUrl + '/' + page + '/rows', '').then(
                (result: ng.IHttpPromiseCallbackArg<IHalElement<Models.IGridPage>>) => {
                    let p: Models.IGridPage = HalConverter.convertElement(result.data, Models.GridPage.Empty);
                    this.burnishPage(p);
                    return p;
                });
        }

        public addColumn(page: number): ng.IPromise<Models.IGridPage> {
            this.monitorPageGridCache[page] = undefined;
            return this.backendService.post(this.monitorPagesBackendUrl + '/' + page + '/columns', '').then(
                (result: ng.IHttpPromiseCallbackArg<IHalElement<Models.IGridPage>>) => {
                    let p: Models.IGridPage = HalConverter.convertElement(result.data, Models.GridPage.Empty);
                    this.burnishPage(p);
                    return p;
                });
        }

        public addGridStream(page: number, row: number, column: number, tx: number, rx: number,
                             type: 'Video'|'SwitchPort'|'Digicon'): ng.IPromise<Models.IGridPage> {
            this.monitorPageGridCache[page] = undefined;
            return this.backendService.post(this.monitorPagesBackendUrl + '/' + page + '/playoutMonitors', {
                row: row,
                column: column,
                source: tx,
                sink: rx,
                type: type,
            }).then((result: ng.IHttpPromiseCallbackArg<Models.IGridPage>) => {
                return result.data;
            });
        }

        // @TODO: PUT when we have the endpoint instead of delete + post
        public updateGridStream(page: number, row: number, column: number, tx: number, rx: number,
                                monitor: Models.IGridMonitor, type: 'Video'|'SwitchPort'|'Digicon'): ng.IPromise<Models.IGridPage> {
            this.monitorPageGridCache[page] = undefined;
            return this.backendService.delete(this.monitorPagesBackendUrl + '/' + page + '/playoutMonitors/' + monitor.id).then(() => {
                return this.backendService.post(this.monitorPagesBackendUrl + '/' + page + '/playoutMonitors', {
                    row: row,
                    column: column,
                    source: tx,
                    sink: rx,
                    type: type,
                }).then((result: ng.IHttpPromiseCallbackArg<Models.IGridPage>) => {
                    return result.data;
                });
            });

        }

        public removeGridStream(page: number, monitor: Models.IGridMonitor): ng.IPromise<void> {
            this.monitorPageGridCache[page] = undefined;
            return this.backendService.delete(this.monitorPagesBackendUrl + '/' + page + '/playoutMonitors/' + monitor.id).then(() => {
                return;
            });
        }

        public addMonitorPage(room: number, numOrder: number): ng.IPromise<Models.IGridPage> {
            this.gridPagesByRoom[room] = undefined;
            let newRoom: Object = {
                columns: 3,
                rows: 3,
                numOrder: numOrder,
                monitorRoom: this.monitoringRoomsBackendUrl + '/' + room,
            };
            return this.backendService.post(this.monitorPagesBackendUrl, newRoom, false, undefined, true).then(
                (result: ng.IHttpPromiseCallbackArg<IHalElement<Models.IGridPage>>) => {
                    let p: Models.IGridPage = HalConverter.convertElement(result.data, Models.GridPage.Empty);
                    this.burnishPage(p);
                    return p;
                });
        }

        public removeMonitorPage(page: number): ng.IPromise<void> {
            //TODO: Guess monitorRoom id and remove pages by room cache
            // if (this.monitorPageGridCache[page] !== undefined) {
            //     const roomId: number = this.monitorPageGridCache[page].monitorRoom.id
            //     this.gridPagesByRoom[roomId] = undefined;
            // }
            //
            this.monitorPageGridCache[page] = undefined;
            return this.backendService.delete(this.monitorPagesBackendUrl + '/' + page).then(
                () => {
                    return;
                });
        }

        public toggleTrending(page: number): ng.IPromise<Models.IGridPage> {
            return this.backendService
                .put(`${this.monitorPagesBackendUrl}/${page}/trending`, '')
                .then((result: ng.IHttpPromiseCallbackArg<IHalElement<Models.IGridPage>>) => {
                    let p: Models.IGridPage = HalConverter.convertElement(result.data, Models.GridPage.Empty);
                    this.burnishPage(p);
                    return p;
                });
        }

        private burnishPage(page: Models.IGridPage): void {
            page.id = Common.Utils.idFromHref(page.href);
        }
        /**
         * END OF NEW METHODS FOR GRID
         */

        private createMonitoringRoom(monitoringRoom: MonitoringRoom): ng.IPromise<MonitoringRoom> {
            this.allGridRooms = undefined;
            return this.backendService
                .post(this.monitoringRoomsBackendUrl, {
                    name: monitoringRoom.name,
                    alias: monitoringRoom.alias,
                }, false, undefined, false)
                .then ((result: ng.IHttpPromiseCallbackArg<IHalElement<MonitoringRoom>>) => {
                    let halElement: IHalElement<MonitoringRoom> = result.data;
                    let converted: MonitoringRoom = HalConverter.convertElement(halElement, MonitoringRoom.Empty);
                    return converted;
                });
        }

        private updateMonitoringRoom(monitoringRoom: MonitoringRoom): ng.IPromise<MonitoringRoom> {
            this.allGridRooms = undefined;
            return this.backendService
                .put(monitoringRoom.href, {
                    name: monitoringRoom.name,
                    creationDate: monitoringRoom.creationDate,
                    defaultForOrganizationId: monitoringRoom.defaultForOrganizationId,
                    alias: monitoringRoom.alias,
                })
                .then ((result: ng.IHttpPromiseCallbackArg<IHalElement<MonitoringRoom>>) => {
                    let halElement: IHalElement<MonitoringRoom> = result.data;
                    let converted: MonitoringRoom = HalConverter.convertElement(halElement, MonitoringRoom.Empty);
                    return converted;
                });
        }

        private getMonitorRoomPromise(href: string): ng.IPromise<MonitoringRoom> {
            return this.backendService
                .get(href)
                .then((data: ng.IHttpPromiseCallbackArg<IHalElement<MonitoringRoom>>) => {
                    let halElement: IHalElement<MonitoringRoom> = data.data;
                    let converted: MonitoringRoom = HalConverter.convertElement(halElement, MonitoringRoom.Empty);
                    return converted;
                }
            );
        }
        
        private createMonitor(monitor: IMonitor): ng.IPromise<IMonitor> {
            return this.backendService
                .post(this.monitorsBackendUrl, monitor)
                .then((result: ng.IHttpPromiseCallbackArg<IHalElement<IMonitor>>) => {
                    let halElement: IHalElement<IMonitor> = result.data;
                    let converted: IMonitor = HalConverter.convertElement(halElement, Models.Monitor.Empty);
                    return converted;
                });
        }

        private updateMonitor(monitor: IMonitor): ng.IPromise<IMonitor> {
            return this.backendService
                .put(monitor.href, {
                    monitoravailableStreamsRoom: monitor.monitorRoom,
                    source: monitor.source,
                    numOrder: monitor.numOrder,
                    name: monitor.name,
                })
                .then((result: ng.IHttpPromiseCallbackArg<IHalElement<IMonitor>>) => {
                    let halElement: IHalElement<IMonitor> = result.data;
                    let converted: IMonitor = HalConverter.convertElement(halElement, Models.Monitor.Empty);
                    converted.monitorRoom = monitor.monitorRoom;
                    return converted;
                });
        }
    }

    interface GridPageMap {
        [key: number]: Models.IMonitorPageGrid;
    }

    interface GridPagesByRoomMap {
        [key: number]: Models.IGridPage[];
    }

}

